import React, { useState } from "react"
import {
	Grid,
	Typography,
	Select,
	MenuItem,
	Divider as MuiDivider
} from "@material-ui/core"
import Alert from "@material-ui/lab/Alert"
import clsx from "clsx"
import {
	RefreshCw as RefreshCwIcon,
	Kanban as KanbanIcon,
	MessageCircle as MessageCircleIcon,
	MessageSquare as MessageSquareIcon,
	Settings as SettingsIcon,
	Shield as ShieldIcon,
	Gift as GiftIcon
} from "lucide-react"

import { Sidebar } from "letalkui"

import LetalkLogo from "@/components/SVGComponents/LetalkLogo"
import LetalkVerticalLogo from "@/components/SVGComponents/LetalkVerticalLogo"

import { useGlobalStateStore } from "@/store/GlobalState"
import { usePermissionStateStore } from "@/store/PermissionState"
import useSocket from "@/hooks/useSocket"
import useProfileMenuItems from "@/hooks/useProfileMenuItems"
import ReferralPopupTrigger from "@/pages/Attendance/ReferralPopup/ReferralPopupTrigger"

import { ChannelType } from "@/protocols/channel"

import { deviceIsMobile } from "@/utils/checkDevice"

import { inboxAppUrl } from "@/config/url"
import { letalkLinks } from "@/utils/link"

import { ActionDialog, Divider, Notification } from "@/components"
import InstanceSelectorDialogue from "@/components/InstanceSelectorDialogue"

import useStyles from "@/pages/Attendance/Sidebar/styles"

export type InboxInnerType = "inbox" | "board" | "lechat" | "admin"

type SyncChatsDialogValuesType = {
	loading: boolean,
	open: boolean,
	syncHour: number
}

type InboxSidebarProps = {
	inboxInner: InboxInnerType
	onInboxClick: () => void
	onBoardClick: () => void
	onLeChatClick: () => void
	isCollapsed: boolean
	onToggleCollapse: () => void
	onProfileInfoOpen: () => void
	onUserSettingsOpen: () => void
	onSecuritySettingsOpen: () => void
	isUserSettingsOpen: boolean
}

const iconSize = 20

const InboxSidebar = (props: InboxSidebarProps) => {
	const {
		inboxInner,
		onInboxClick,
		onBoardClick,
		onLeChatClick,
		isCollapsed,
		onToggleCollapse,
		onUserSettingsOpen,
		isUserSettingsOpen
	} = props

	const sidebarClasses = useStyles()

	const globalStateStore = useGlobalStateStore()
	const { checkPermission } = usePermissionStateStore()
	const socket = useSocket()
	const { handleLogout, profileMenuAccountItems } = useProfileMenuItems()

	const [syncChatsDialogValues, setSyncChatsDialogValues] = useState<SyncChatsDialogValuesType>({
		loading: false,
		open: false,
		syncHour: 0
	})

	const userStore = globalStateStore.user
	const instanceStore = globalStateStore.instance
	const canAccessManagerPanel = checkPermission("manager-panel", "access")
	const canAccessLechat = !!globalStateStore.instance.subscriptionData?.limits?.permission?.lechat
	const isBoardActive = inboxInner === "board" && !isUserSettingsOpen
	const isLeChatActive = inboxInner === "lechat" && !isUserSettingsOpen
	const isInboxActive = inboxInner === "inbox" && !isUserSettingsOpen

	const handleSyncChatsMenuOption = () => {
		setSyncChatsDialogValues({ ...syncChatsDialogValues, open: true })
	}

	const handleRunSyncChats = async () => {
		setSyncChatsDialogValues({ ...syncChatsDialogValues, loading: true })

		try {
			if (syncChatsDialogValues.syncHour && globalStateStore.currentChannel) {
				const actDate = new Date()
				actDate.setHours(actDate.getHours() - syncChatsDialogValues.syncHour)

				await socket.scheduleManyChatMessagesSync({
					channelType: globalStateStore.currentChannel?.channelType as ChannelType,
					inboxChannelId: globalStateStore.currentChannel?.id as number,
					afterDate: Number(actDate)
				})

				Notification.success({ message: "O processo de sincronização foi iniciado." })
			}
		} catch (error) {
			Notification.error({ message: "Houve um erro ao tentar sincronizar as conversas." })
		}

		setSyncChatsDialogValues({ ...syncChatsDialogValues, loading: false })
	}

	const handleOpenMultipleInstancesDialogue = () => {
		InstanceSelectorDialogue.open({
			actualUserInstanceId: globalStateStore.instance.instance_id,
			allUserInstances: globalStateStore.user.user_instances,
			isAdminView: globalStateStore?.user?.extra_data?.is_admin_mode
		})
	}

	const handleOpenAdminPanel = () => {
		if (!deviceIsMobile()) {
			window.open(`${inboxAppUrl.cpURL}/admin`, "inbox_admin_page")
		}
	}

	const handleHelpClick = () => {
		window.open(letalkLinks.wikiMainPage, "_blank")
	}

	return (
		<>
			<Sidebar.Container
				variant="inbox"
				isCollapsed={isCollapsed}
			>
				<Sidebar.Header
					logo={<LetalkLogo isDarkLogo className={sidebarClasses.logo} />}
					collapsedLogo={<LetalkVerticalLogo isDarkLogo className={sidebarClasses.collapsedLogo} />}
					variant="inbox"
					isCollapsed={isCollapsed}
					onToggleCollapse={onToggleCollapse}
				/>

				<Sidebar.Nav
					variant="inbox"
					isCollapsed={isCollapsed}
					onHelpClick={handleHelpClick}
				>
					<Sidebar.NavItem
						icon={<MessageCircleIcon size={iconSize} className={clsx(sidebarClasses.icon, isInboxActive && sidebarClasses.iconActive)} />}
						label="Inbox"
						isActive={isInboxActive}
						isCollapsed={isCollapsed}
						onClick={onInboxClick}
						variant="inbox"
					/>

					<Sidebar.NavItem
						icon={<KanbanIcon size={iconSize} className={clsx(sidebarClasses.icon, isBoardActive && sidebarClasses.iconActive)} />}
						label="Funil"
						isActive={isBoardActive}
						isCollapsed={isCollapsed}
						onClick={onBoardClick}
						variant="inbox"
					/>

					{canAccessLechat && (
						<Sidebar.NavItem
							icon={<MessageSquareIcon size={iconSize} className={clsx(sidebarClasses.icon, isLeChatActive && sidebarClasses.iconActive)} />}
							label="LeChat"
							isActive={isLeChatActive}
							isCollapsed={isCollapsed}
							onClick={onLeChatClick}
							variant="inbox"
						/>
					)}

					{canAccessManagerPanel && (
						<Sidebar.NavItem
							icon={<ShieldIcon size={iconSize} />}
							label="Painel do Gestor"
							isCollapsed={isCollapsed}
							onClick={handleOpenAdminPanel}
							variant="inbox"
						/>
					)}

					{globalStateStore.isBaileysWhatsappChannel && (
						<Sidebar.NavItem
							icon={<RefreshCwIcon size={iconSize} />}
							label="Sincronizar Conversas"
							isCollapsed={isCollapsed}
							onClick={handleSyncChatsMenuOption}
							variant="inbox"
						/>
					)}

					<Sidebar.NavItem
						icon={<GiftIcon size={iconSize} />}
						label="Indique e Ganhe"
						isCollapsed={isCollapsed}
						onClick={() => ReferralPopupTrigger.open()}
						variant="inbox"
					/>

					<Sidebar.NavItem
						icon={<SettingsIcon size={iconSize} className={clsx(sidebarClasses.icon, isUserSettingsOpen && sidebarClasses.iconActive)} />}
						label="Configurações"
						isActive={isUserSettingsOpen}
						isCollapsed={isCollapsed}
						onClick={onUserSettingsOpen}
						variant="inbox"
					/>
				</Sidebar.Nav>

				<MuiDivider className={sidebarClasses.divider} />

				<Sidebar.Footer>
					<Sidebar.SwitchInstanceButton
						instanceId={String(instanceStore.instance_id)}
						instanceName={instanceStore.nickname}
						onClick={handleOpenMultipleInstancesDialogue}
						isCollapsed={isCollapsed}
						variant="inbox"
					/>

					<Sidebar.ProfileMenu
						username={userStore?.name ?? ""}
						email={userStore.email}
						avatarUrl={undefined}
						onClick={handleLogout}
						variant="inbox"
						isCollapsed={isCollapsed}
						accountItems={profileMenuAccountItems}
					/>
				</Sidebar.Footer>
			</Sidebar.Container>

			<ActionDialog
				title="Sincronização de conversas"
				openDialog={syncChatsDialogValues.open}
				fullWidth
				saveText="SINCRONIZAR"
				onSave={handleRunSyncChats}
				loading={syncChatsDialogValues.loading}
				onClose={() => setSyncChatsDialogValues({ ...syncChatsDialogValues, open: false })}
				saveButtonDisabled={!syncChatsDialogValues.syncHour}
			>
				<Grid container spacing={4}>
					<Grid item>
						<Alert color="warning" icon={false} style={{ borderRadius: "6px" }}>
							<Typography align="justify">
								A sincronização é um processo demorado e pode levar vários minutos. Antes de executar, tenha certeza de que é realmente necessário.
							</Typography>
							<Divider orientation="horizontal" size={3}/>
							<Typography align="justify">
								Indicamos em casos que você tenha recebido a mensagem no celular mas não tenha recebido na Letalk. Apesar de raro, é possível que aconteça.
							</Typography>
							<Divider orientation="horizontal" size={3}/>
							<Typography align="justify">
								Caso após executar esse processo a mensagem não aparecer, certifique-se de que a mensagem esteja aparecendo no Whatsapp Web. Em alguns casos, a mensagem aparece no celular mas não aparece no Whatsapp Web, o que acarreta nesse problema.
							</Typography>
						</Alert>
					</Grid>
					<Grid item xs={8}>
						<Typography variant="body1">
							<b> Sincronizar as últimas: </b>
						</Typography>

						<Divider size={1} orientation="horizontal" />

						<Select
							value={syncChatsDialogValues.syncHour}
							onChange={(event) => setSyncChatsDialogValues({ ...syncChatsDialogValues, syncHour: event.target.value as number })}
							variant="outlined"
							fullWidth
						>
							<MenuItem value={0} disabled hidden>
								Selecione
							</MenuItem>
							<MenuItem value={12}>
								12 Horas
							</MenuItem>

							<MenuItem value={24}>
								24 Horas
							</MenuItem>

							<MenuItem value={36}>
								36 Horas
							</MenuItem>
						</Select>
					</Grid>
				</Grid>
			</ActionDialog>
		</>
	)
}

export default InboxSidebar
